"use client";

import {
  Archive,
  FolderOpen,
  FileSpreadsheet,
  ImageIcon,
  AlertTriangle,
  CheckCircle,
  Info,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const ZipFileValidation = () => {
  const { t, i18n } = useTranslation();
  return (
    <div className="space-y-6">
      {/* File Requirements */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-slate-50 to-gray-100 border-b border-gray-200 p-4">
          <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
            <Archive className="w-5 h-5" />
            {t("File Upload Requirements")}
          </h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold text-sm text-blue-900">
                {t("File Types")}
              </h4>
              <p className="text-xs text-blue-700">{t(".zip, .xls, .xlsx")}</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <h4 className="font-semibold text-sm text-green-900">
                {t("File Size")}
              </h4>
              <p className="text-xs text-green-700">{t("Maximum 50MB")}</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <h4 className="font-semibold text-sm text-purple-900">
                {t("Required")}
              </h4>
              <p className="text-xs text-purple-700">{t("Cannot be empty")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Zip Structure */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-slate-50 to-gray-100 border-b border-gray-200 p-4">
          <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            {t("Required Zip Structure")}
          </h3>
        </div>
        <div className="p-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <div className="font-mono text-sm space-y-2">
              <div className="flex items-center gap-2 text-gray-800">
                <Archive className="w-4 h-4 text-blue-600" />
                <span className="font-semibold">{t("your-file.zip")}</span>
              </div>
              <div className="ml-6 flex items-center gap-2 text-gray-700">
                <FolderOpen className="w-4 h-4 text-yellow-600" />
                <span className="font-semibold">{t("images/")}</span>
                <span className="text-xs text-gray-500">
                  {t("(all employee images)")}
                </span>
              </div>
              <div className="ml-6 flex items-center gap-2 text-gray-700">
                <FileSpreadsheet className="w-4 h-4 text-green-600" />
                <span className="font-semibold">{t("employee.xlsx")}</span>
                <span className="text-xs text-gray-500">
                  {t("(employee data)")}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-900 text-xs">
                  {t("Images Folder")}
                </h4>
                <p className="text-blue-800 text-xs">
                  {t('Must be named "images" (lowercase)')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-green-900 text-xs">
                  {t("Excel File")}
                </h4>
                <p className="text-green-800 text-xs">
                  {t('Must be named "employee.xlsx"')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Naming */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-slate-50 to-gray-100 border-b border-gray-200 p-4">
          <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            {t("Image Naming Rules")}
          </h3>
        </div>
        <div className="p-4 space-y-4">
          {/* Simple Example */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 text-sm mb-3">
              {t("How Image Linking Works:")}
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">
                  {t("1. Put image in images folder:")}
                </span>
                <code className="bg-white px-2 py-1 rounded border text-xs">
                  {t("ahmed_iqama_front.jpg")}
                </code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">
                  {t("2. In Excel, find Ahmed's row:")}
                </span>
                <span className="text-xs text-gray-500">
                  {t("Employee: Ahmed Ali")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">
                  {t('3. Go to "Iqama Front Document" column:')}
                </span>
                <span className="text-xs text-gray-500">
                  {t("Enter exact image name")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">
                  {t("4. Type the exact filename:")}
                </span>
                <code className="bg-white px-2 py-1 rounded border text-xs">
                  {t("ahmed_iqama_front.jpg")}
                </code>
              </div>
              <div className="flex items-center justify-center gap-2 text-green-600 pt-2 border-t border-gray-200">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium text-sm">
                  {t("✓ Image will be linked to Ahmed's Iqama document")}
                </span>
              </div>
            </div>

            {/* Additional clarification */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-xs text-blue-800">
                <strong>{t("Key Point:")}</strong>{" "}
                {t(
                  'Each document type (Iqama, Passport, etc.) has separate "Front Document" and "Back Document" columns in the Excel file. Enter the exact image filename in the appropriate column for each employee.'
                )}
              </p>
            </div>
          </div>

          {/* Document Types */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-semibold text-amber-900 text-sm mb-2">
              {t("Document Types (Front & Back):")}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <span className="text-amber-800">• {t("Iqama")}</span>
              <span className="text-amber-800">• {t("National ID")}</span>
              <span className="text-amber-800">• {t("Passport")}</span>
              <span className="text-amber-800">• {t("Bank Account")}</span>
              <span className="text-amber-800">• {t("Health Insurance")}</span>
              <span className="text-amber-800">• {t("Health Card")}</span>
              <span className="text-amber-800">• {t("Driving License")}</span>
              <span className="text-amber-800">• {t("Contract")}</span>
            </div>
          </div>

          {/* Important Rules */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-red-900 text-sm">
                  {t("Important Rules:")}
                </h4>
                <ul className="text-red-800 text-xs mt-1 space-y-1">
                  <li>• {t("Names must match exactly (case-sensitive)")}</li>
                  <li>• {t("Include file extensions (.jpg, .png, .pdf)")}</li>
                  <li>• {t("No spaces or special characters in names")}</li>
                  <li>
                    •{" "}
                    {t(
                      "Each document section has Front & Back columns in Excel"
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZipFileValidation;
